import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useAuth } from '../auth/context';
import { readEnv } from '../env';
import { openControlWs, type ControlWs } from '../session/control-ws';
import { openMediaWs, buildMediaUrl, type MediaWs } from '../session/media-ws';
import { createWebRtcAudioSubscriber, type WebRtcAudioSubscriber } from '../session/webrtc-audio-subscriber';
import { makeCanvasRenderTarget, makeFrameRouter } from '../session/frame-decoder';
import { makeCanvasH264Target, makeH264Router, webCodecsSupported } from '../session/h264-decoder';
import {
  encodeKey, encodeMouseButton, encodeMouseMove, encodeMouseWheel, makeMoveThrottle,
} from '../session/input-encoder';
import { initialSessionViewState, sessionViewReducer } from '../session/reducer';
import { readStageWidthPref, writeStageWidthPref } from '../session/encoding-pref';
import { ToastOnEvent } from '../components/Toast';
import { Button } from '../components/Button';
import { Kbd } from '../components/Kbd';
import { StatusOverlay } from '../components/StatusOverlay';

const MIN_STAGE_WIDTH = 320;

interface CanvasSize { w: number; h: number; }

interface VideoRouter {
  ingest: (buf: ArrayBuffer) => Promise<void>;
  stats: () => { fps: number; framesDropped: number };
  reset: () => void;
  close?: () => void;
}

export function SessionPage() {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const mediaTokenRef = useRef<string | null>(params.get('mediaToken'));
  const hostName = params.get('hostName') ?? 'host';
  const codecRef = useRef<'jpeg' | 'h264'>(((params.get('codec') as 'jpeg' | 'h264' | null) ?? 'jpeg'));
  const auth = useAuth();
  const navigate = useNavigate();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const controlRef = useRef<ControlWs | null>(null);
  const mediaRef = useRef<MediaWs | null>(null);
  const routerRef = useRef<VideoRouter | null>(null);
  const audioSubRef = useRef<WebRtcAudioSubscriber | null>(null);
  const [hostAudioEnabled, setHostAudioEnabled] = useState(true);
  const [hostAudioState, setHostAudioState] = useState<RTCPeerConnectionState | null>(null);
  const moveThrottle = useRef(makeMoveThrottle(1000 / 60));
  const [state, dispatch] = useReducer(sessionViewReducer, initialSessionViewState);
  const [size, setSize] = useState<CanvasSize | null>(null);
  const [localCursor, setLocalCursor] = useState<{ x: number; y: number } | null>(null);
  const [hostCursor, setHostCursor] = useState<{ x: number; y: number } | null>(null);
  const [stageWidth, setStageWidth] = useState<number | null>(() => readStageWidthPref());
  const stageRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);

  const startStageResize = (e: React.MouseEvent): void => {
    e.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    dragRef.current = { startX: e.clientX, startW: stage.getBoundingClientRect().width };
    const onMove = (ev: MouseEvent): void => {
      if (!dragRef.current) return;
      const delta = ev.clientX - dragRef.current.startX;
      const next = Math.max(MIN_STAGE_WIDTH, dragRef.current.startW + delta);
      setStageWidth(next);
    };
    const onUp = (): void => {
      if (dragRef.current && stageRef.current) {
        writeStageWidthPref(stageRef.current.getBoundingClientRect().width);
      }
      dragRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const resetStageWidth = (): void => {
    setStageWidth(null);
    writeStageWidthPref(null);
  };
  // When the local browser mouse is over the canvas, prefer that (zero latency).
  // Otherwise fall back to the host's reported cursor position so the user can
  // still see where the host's cursor is (e.g., someone at the host machine
  // moved it directly).
  const cursorPos = localCursor ?? hostCursor;

  useEffect(() => {
    if (mediaTokenRef.current) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const teardown = useCallback(() => {
    try { controlRef.current?.close(); } catch { /* ignore */ }
    try { mediaRef.current?.close(); } catch { /* ignore */ }
    controlRef.current = null;
    mediaRef.current = null;
  }, []);

  useEffect(() => {
    const jwt = auth.getJwt();
    const mediaToken = mediaTokenRef.current;
    if (!jwt || !mediaToken || !sessionId) {
      void navigate('/hosts', { replace: true });
      return;
    }
    const env = readEnv();
    const wsBase = env.relayBaseUrl.replace(/^http/, 'ws');
    const canvas = canvasRef.current;
    if (!canvas) return;

    const codec = codecRef.current;
    let router: VideoRouter;
    if (codec === 'h264') {
      if (!webCodecsSupported) {
        dispatch({ type: 'error', message: 'webcodecs_unsupported' });
        return;
      }
      const baseTarget = makeCanvasH264Target(canvas);
      const target = {
        draw(frame: VideoFrame) {
          const w = frame.displayWidth;
          const h = frame.displayHeight;
          baseTarget.draw(frame);
          setSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
        },
      };
      router = makeH264Router(target);
    } else {
      const baseTarget = makeCanvasRenderTarget(canvas);
      const target = {
        draw(bitmap: ImageBitmap) {
          const w = bitmap.width;
          const h = bitmap.height;
          baseTarget.draw(bitmap);
          setSize((prev) => (prev && prev.w === w && prev.h === h ? prev : { w, h }));
        },
      };
      router = makeFrameRouter(target);
    }
    routerRef.current = router;

    const abort = new AbortController();

    const sessionIdNum = Number.parseInt(sessionId, 10);
    if (!Number.isFinite(sessionIdNum) || sessionIdNum <= 0) {
      void navigate('/hosts', { replace: true });
      return;
    }

    // eslint-disable-next-line no-console
    console.info(`[session] mount sessionId=${sessionIdNum} codec=${codec} mediaTokenPrefix=${mediaToken.slice(0, 8)} hostName=${hostName}`);

    void (async () => {
      try {
        const control = await openControlWs(`${wsBase}/ws/control`, jwt, sessionIdNum, {
          onAck: () => { /* dispatched after media opens */ },
          onAuthError: () => { dispatch({ type: 'error', message: 'authentication_failed' }); },
          onSessionEnded: (reason) => { dispatch({ type: 'session-ended', reason }); },
          onClose: () => { /* handled by error/ended branches */ },
          onError: () => { /* logged below */ },
          onCursorPosition: (nx, ny) => {
            const cv = canvasRef.current;
            if (!cv) return;
            const rect = cv.getBoundingClientRect();
            setHostCursor({ x: nx * rect.width, y: ny * rect.height });
          },
          // Host-side audio-publisher starts offering as soon as the
          // session goes live. Create the subscriber on-demand (lazy, so
          // sessions without audio don't construct a PeerConnection).
          onWebRtcOffer: (sdp) => {
            if (!audioRef.current || !controlRef.current) return;
            if (!audioSubRef.current) {
              audioSubRef.current = createWebRtcAudioSubscriber({
                sessionId: sessionIdNum,
                control: controlRef.current,
                audioEl: audioRef.current,
                onStateChange: (state) => setHostAudioState(state),
                // eslint-disable-next-line no-console
                onError: (err) => console.warn('[webrtc-audio] error', err),
              });
            }
            void audioSubRef.current.handleOffer(sdp);
          },
          onWebRtcIce: (c) => {
            if (audioSubRef.current) void audioSubRef.current.handleIce(c);
          },
        }, abort.signal);
        if (abort.signal.aborted) { control.close(); return; }
        controlRef.current = control;

        const media = await openMediaWs(buildMediaUrl(env.relayBaseUrl, mediaToken), {
          onFrame: (buf) => { void router.ingest(buf); },
          onClose: () => { /* handled */ },
          onError: () => { /* handled */ },
        }, abort.signal);
        if (abort.signal.aborted) { media.close(); control.close(); return; }
        mediaRef.current = media;
        dispatch({ type: 'connected' });
      } catch {
        if (!abort.signal.aborted) dispatch({ type: 'error', message: 'connect_failed' });
      }
    })();

    const statsTimer = window.setInterval(() => {
      const r = routerRef.current;
      if (!r) return;
      const s = r.stats();
      dispatch({ type: 'tick-stats', fps: s.fps, framesDropped: s.framesDropped });
    }, 1000);

    const pingTimer = window.setInterval(() => {
      controlRef.current?.sendPing();
    }, 15_000);

    return () => {
      abort.abort();
      window.clearInterval(statsTimer);
      window.clearInterval(pingTimer);
      try { routerRef.current?.close?.(); } catch { /* ignore */ }
      routerRef.current = null;
      try { audioSubRef.current?.close(); } catch { /* ignore */ }
      audioSubRef.current = null;
      teardown();
    };
  }, [auth, sessionId, navigate, teardown, hostName]);

  useEffect(() => {
    if (state.kind === 'ended' || state.kind === 'error') {
      const t = window.setTimeout(() => { void navigate('/hosts', { replace: true }); }, 1500);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [state.kind, navigate]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || state.kind !== 'live') return;
    canvas.tabIndex = 0;
    canvas.focus();

    const onMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const localX = e.clientX - rect.left;
      const localY = e.clientY - rect.top;
      setLocalCursor({ x: localX, y: localY });
      if (!moveThrottle.current.tick()) return;
      controlRef.current?.sendInputMouse(encodeMouseMove(
        { clientX: localX, clientY: localY, buttons: e.buttons },
        { width: rect.width, height: rect.height },
        Date.now(),
      ));
    };
    const onMouseLeave = () => setLocalCursor(null);
    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      controlRef.current?.sendInputMouse(encodeMouseButton(
        { clientX: e.clientX - rect.left, clientY: e.clientY - rect.top, button: e.button },
        { width: rect.width, height: rect.height },
        true,
        Date.now(),
      ));
    };
    const onMouseUp = (e: MouseEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      controlRef.current?.sendInputMouse(encodeMouseButton(
        { clientX: e.clientX - rect.left, clientY: e.clientY - rect.top, button: e.button },
        { width: rect.width, height: rect.height },
        false,
        Date.now(),
      ));
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      controlRef.current?.sendInputMouse(encodeMouseWheel({ deltaX: e.deltaX, deltaY: e.deltaY }, Date.now()));
    };
    const onKeyDown = (e: KeyboardEvent) => {
      e.preventDefault();
      controlRef.current?.sendInputKey(encodeKey(e, true, Date.now()));
    };
    const onKeyUp = (e: KeyboardEvent) => {
      e.preventDefault();
      controlRef.current?.sendInputKey(encodeKey(e, false, Date.now()));
    };
    const onContextMenu = (e: MouseEvent) => e.preventDefault();
    const onClickFocus = () => canvas.focus();

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mouseleave', onMouseLeave);
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('keydown', onKeyDown);
    canvas.addEventListener('keyup', onKeyUp);
    canvas.addEventListener('contextmenu', onContextMenu);
    canvas.addEventListener('click', onClickFocus);

    return () => {
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('keydown', onKeyDown);
      canvas.removeEventListener('keyup', onKeyUp);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('click', onClickFocus);
    };
  }, [state.kind]);

  const fps = state.kind === 'live' ? state.fps : 0;
  const dropped = state.kind === 'live' ? state.framesDropped : 0;

  const statusText =
    state.kind === 'live' ? 'streaming'
      : state.kind === 'connecting' ? 'connecting…'
      : state.kind === 'reconnecting' ? `reconnecting (attempt ${state.attempt})`
      : state.kind === 'ended' ? `session ended · ${state.reason}`
      : 'connection error';

  return (
    <main className="grid h-screen w-screen grid-rows-[auto_1fr_auto] overflow-hidden">
      <ToastOnEvent when={state.kind === 'ended'} kind="info" text="Session ended by host" />
      <ToastOnEvent when={state.kind === 'error'} kind="error" text="Connection error" />

      <header className="flex items-center gap-4 border-b border-border-subtle bg-bg-raised/70 px-6 py-3 backdrop-blur">
        <div className="flex items-baseline gap-3">
          <span className="font-[var(--font-serif)] text-xl font-bold italic tracking-tight">
            <span className="font-sans not-italic font-medium text-accent">▸ </span>
            remote.console
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-text-secondary">
            {hostName}
          </span>
        </div>
        <div className="flex-1" />
        <Button variant="secondary" size="sm" onClick={resetStageWidth} title="Fit canvas to viewport">
          Fit
        </Button>
        {hostAudioState ? (
          <Button
            variant="secondary"
            size="sm"
            title={`Host audio (WebRTC): ${hostAudioState}`}
            onClick={() => {
              const next = !hostAudioEnabled;
              setHostAudioEnabled(next);
              audioSubRef.current?.setEnabled(next);
            }}
          >
            {hostAudioEnabled ? '♪ Audio on' : '✕ Audio off'}
          </Button>
        ) : null}
        <Button
          variant="danger"
          size="sm"
          onClick={() => { teardown(); void navigate('/hosts', { replace: true }); }}
        >
          Disconnect
        </Button>
      </header>

      {/* Hidden audio sink for the WebRTC audio-only subscriber. The
          MediaStream from the host's PeerConnection attaches here when
          the first track arrives; the toggle in the header controls its
          enabled state. */}
      <audio ref={audioRef} autoPlay playsInline hidden />


      <section className="relative flex items-center justify-center overflow-auto p-4">
        <div
          ref={stageRef}
          className="relative overflow-hidden rounded-[var(--radius-md)] border border-border-subtle bg-black shadow-[var(--shadow-3)]"
          style={stageWidth ? { width: stageWidth } : { maxWidth: '100%' }}
        >
          <canvas
            ref={canvasRef}
            className="block cursor-none outline-none"
            style={{
              width: stageWidth ? '100%' : 'auto',
              height: 'auto',
              maxWidth: '100%',
              maxHeight: stageWidth ? 'none' : 'calc(100vh - 128px)',
              aspectRatio: size ? `${size.w} / ${size.h}` : 'auto',
              objectFit: 'contain',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 mix-blend-overlay"
            style={{ background: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.012) 0 1px, transparent 1px 3px)' }}
          />
          <div
            role="separator"
            aria-label="Resize canvas"
            title="Drag to resize"
            onMouseDown={startStageResize}
            className="absolute right-[-6px] top-0 z-10 flex h-full w-3 cursor-ew-resize items-center justify-center"
          >
            <div className="h-10 w-[3px] rounded-sm bg-border-strong transition-colors hover:bg-accent" />
          </div>
          {cursorPos && state.kind === 'live' && (
            <svg
              aria-hidden
              width="20"
              height="28"
              viewBox="0 0 20 28"
              className="pointer-events-none absolute"
              style={{ left: cursorPos.x, top: cursorPos.y, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))' }}
            >
              <path d="M2 2 L2 22 L7 17 L10 24 L13 23 L10 16 L17 16 Z" fill="white" stroke="black" strokeWidth="1.5" strokeLinejoin="round" />
            </svg>
          )}
        </div>

        {state.kind === 'connecting' && (
          <StatusOverlay kind="connecting" title="connecting…" />
        )}
        {state.kind === 'reconnecting' && (
          <StatusOverlay kind="reconnecting" title="reconnecting" caption={`attempt ${state.attempt}`} />
        )}
        {state.kind === 'ended' && (
          <StatusOverlay kind="ended" title="Session ended" caption={state.reason} />
        )}
        {state.kind === 'error' && (
          <StatusOverlay kind="error" title="Connection error" caption={state.message} />
        )}
      </section>

      <footer className="flex items-center gap-5 border-t border-border-subtle bg-bg-raised/70 px-6 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-text-muted">
        <span
          className={
            state.kind === 'live'
              ? 'h-2 w-2 rounded-full bg-accent shadow-[var(--shadow-accent)]'
              : state.kind === 'error' || state.kind === 'ended'
              ? 'h-2 w-2 rounded-full bg-danger shadow-[0_0_10px_var(--color-danger)]'
              : 'h-2 w-2 rounded-full bg-text-muted animate-[pulse-dot_1.6s_ease-in-out_infinite]'
          }
        />
        <span>{statusText}</span>
        <span className="flex-1" />
        <span className="text-text-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>{fps} fps</span>
        <span className="text-text-primary" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {size ? `${size.w} × ${size.h}` : '— × —'}
        </span>
        {dropped > 0 && (
          <span className="text-danger" style={{ fontVariantNumeric: 'tabular-nums' }}>{dropped} dropped</span>
        )}
        <span className="hidden md:flex items-center gap-1.5">
          <Kbd>click</Kbd> <span>·</span> <Kbd>type</Kbd> <span>to send keys</span>
        </span>
      </footer>
    </main>
  );
}
